import React, { useState, useEffect, useCallback } from 'react'
import { IoMailOutline, IoWarningOutline } from 'react-icons/io5'
import {
  IoMdClose,
  IoIosInformationCircleOutline,
  IoMdCheckmark,
} from 'react-icons/io'
import {
  LuUsers,
  LuBuilding,
  LuCoffee,
  LuFileText,
  LuUnlink,
  LuChevronsUpDown,
} from 'react-icons/lu'
import Moment from 'moment-timezone'

import { Modal } from '@components/common/modal'
import { Input } from '@components/common/form'
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@components/common'

import { User, AccountSearchParams } from './MarketplaceUsersPage'
import {
  IndexAccount,
  LinkedLocation,
  LinkedAccount,
  LinkedClientReportingProgram,
  ApiDinerProfile,
  MarketplaceUser,
  UpdateMarketplaceUserParams,
} from '@types'

interface EditMarketplaceUserModalProps {
  user: User
  uri: string
  hideModal: () => void
  sendWelcomeEmail: (params: { marketplaceUserId: string }) => Promise<void>
  updateMarketplaceUser: (
    params: UpdateMarketplaceUserParams,
  ) => Promise<MarketplaceUser | null>
  refreshMarketplaceUsers: (page: number) => void
  unlinkAccount: (
    accountId: string,
    marketplaceUserId: string,
  ) => Promise<boolean>
  loadAccounts: (params: AccountSearchParams) => Promise<IndexAccount[]>
}

const EditMarketplaceUserModal: React.FC<EditMarketplaceUserModalProps> = ({
  user,
  uri,
  hideModal,
  sendWelcomeEmail,
  updateMarketplaceUser,
  refreshMarketplaceUsers,
  unlinkAccount,
  loadAccounts,
}) => {
  const { marketplaceUser, dinerProfiles, clientReportingPrograms } = user
  const linkedLocations = marketplaceUser.marketplaceUserAccounts.reduce(
    (acc, account) => {
      const linkedAccount: LinkedAccount = {
        ...account,
        clientReportingPrograms: clientReportingPrograms
          .filter((crp) => crp.clientId === account.id)
          .map((crp) => {
            return {
              ...crp,
              dinerProfiles: dinerProfiles.filter(
                (dp) => dp.id && crp.dinerProfileIds.includes(dp.id),
              ),
            }
          }),
        dinerProfiles: dinerProfiles.filter((dp) =>
          clientReportingPrograms.every(
            (crp) => dp.id && !crp.dinerProfileIds.includes(dp.id),
          ),
        ),
      }

      account.headquarters.forEach((hq) => {
        if (!acc[hq.id]) {
          acc[hq.id] = {
            name: hq.name,
            accounts: [linkedAccount],
          }
        } else {
          acc[hq.id].accounts.push(linkedAccount)
        }
      })

      return acc
    },
    {} as Record<number, LinkedLocation>,
  )

  const [firstName, setFirstName] = useState(marketplaceUser.firstName)
  const [lastName, setLastName] = useState(marketplaceUser.lastName)
  const [email, setEmail] = useState(marketplaceUser.email)

  const [linkedDinerProfiles, setLinkedDinerProfiles] = useState<
    ApiDinerProfile[] | null
  >(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [openCombobox, setOpenCombobox] = useState(false)
  const [accounts, setAccounts] = useState<IndexAccount[]>([])
  const [selectedClient, setSelectedClient] = useState<IndexAccount | null>(
    null,
  )

  const searchAccounts = useCallback(async () => {
    const accounts = await loadAccounts({
      search: searchTerm,
      searchAllHqs: true,
      page: 1,
      resultsPerPage: 10,
    })
    setAccounts(accounts)
  }, [loadAccounts, searchTerm])

  useEffect(() => {
    searchAccounts()
  }, [searchAccounts])

  const onUnlinkAccount = async (client: LinkedAccount) => {
    const { clientReportingPrograms, dinerProfiles } = client

    if (
      clientReportingPrograms.some((crp) => crp.dinerProfiles.length > 0) ||
      dinerProfiles.length > 0
    ) {
      setLinkedDinerProfiles([
        ...clientReportingPrograms.flatMap((crp) => crp.dinerProfiles),
        ...dinerProfiles,
      ])

      const modal = document.getElementById('unlink-account-warning-modal')
      if (modal) {
        ;(modal as HTMLDialogElement).showModal()
      }

      return
    }

    if (!marketplaceUser.id) {
      return
    }

    const unlinked = await unlinkAccount(client.id, marketplaceUser.id)
    if (unlinked) {
      refreshMarketplaceUsers(1)
      hideModal()
    }
  }

  const cancelUnlinkAccount = () => {
    setLinkedDinerProfiles(null)

    const modal = document.getElementById('unlink-account-warning-modal')
    if (modal) {
      ;(modal as HTMLDialogElement).close()
    }
  }

  const navigateToAccount = (accountId: string) => {
    window.open(`${uri}/clients/?account_id=${accountId}&tab=dinerProfiles`)
  }

  const renderHeader = () => {
    const { welcomeEmailSentAt, confirmedAt, isPasswordSet } = marketplaceUser
    const welcomeEmailSent = !!welcomeEmailSentAt
    const confirmed = !!confirmedAt

    return (
      <>
        <div className="flex flex-col items-center w-full mb-3">
          <div className="flex flex-row justify-between items-center w-full">
            <p className="text-2xl font-bold">Edit Marketplace User</p>
            <button
              className="bg-transparent border-none cursor-pointer"
              onClick={hideModal}
            >
              <IoMdClose size={30} />
            </button>
          </div>
          <p className="text-sm text-gray-500 w-full text-left">
            Update user information and account linkages
          </p>
        </div>
        <div
          className="flex flex-col items-center mt-2 p-2 rounded-lg"
          style={{ border: '1px solid #CCCCCC' }}
        >
          <div className="flex flex-row justify-start items-center w-full">
            <p className="text-lg font-bold">Account Status</p>
          </div>
          <div className="flex flex-row gap-2 mt-3">
            {renderStatusTag(
              welcomeEmailSent,
              'Welcome Email',
              welcomeEmailSent ? 'Sent' : 'Not Sent',
            )}
            {renderStatusTag(
              confirmed,
              'Email',
              confirmed ? 'Verified' : 'Pending',
            )}
            {renderStatusTag(
              isPasswordSet,
              'Account',
              isPasswordSet ? 'Complete' : 'Incomplete',
            )}
          </div>
          {welcomeEmailSent && !confirmed && (
            <div
              className="mb-2 mt-4 bg-blue-200 rounded-lg p-2 border border-blue-500 flex flex-row items-start"
              style={{ maxWidth: '80%' }}
            >
              <div>
                <IoIosInformationCircleOutline size={20} color="#3182ce" />
              </div>
              <p className="text-sm text-blue-600 ml-2">
                A welcome email has been sent, but the user has not verified
                their email address yet. The user must click the verification
                link in the welcome email to complete their account setup.
              </p>
            </div>
          )}
          {!confirmed && (
            <div
              className="flex flex-row justify-between items-center pt-3 mt-5 w-full"
              style={{ borderTop: '1px solid #CCCCCC' }}
            >
              <p
                className="text-md"
                style={{ color: '#BBBBBB', fontWeight: 600 }}
              >
                {!welcomeEmailSent
                  ? 'No welcome email been sent to this user yet.'
                  : 'A welcome email has been sent to this user.'}
              </p>
              <button
                className={`font-semibold rounded-lg px-4 py-2 flex flex-row items-center gap-2 ${
                  !welcomeEmailSent
                    ? 'bg-black text-white '
                    : 'bg-white text-black border border-black '
                }`}
                onClick={() => {
                  if (!user.marketplaceUser.id) {
                    return
                  }

                  sendWelcomeEmail({
                    marketplaceUserId: user.marketplaceUser.id,
                  })
                }}
              >
                <IoMailOutline
                  size={20}
                  color={!welcomeEmailSent ? 'white' : 'black'}
                />{' '}
                {!welcomeEmailSent
                  ? 'Send Welcome Email'
                  : 'Resend Welcome Email'}
              </button>
            </div>
          )}
        </div>
      </>
    )
  }

  const renderDinerProfiles = (
    dinerProfiles: ApiDinerProfile[],
    noIcon = false,
  ) => {
    if (dinerProfiles.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          No diner profiles linked to this reporting program
        </div>
      )
    }

    return (
      <div className="space-y-2">
        {!noIcon && (
          <div className="text-sm font-semibold flex items-center gap-2">
            <LuCoffee size={20} color="#a0aec0" />
            Diner Profiles:
          </div>
        )}
        <div className="pl-6 space-y-2">
          {dinerProfiles.map((dinerProfile: ApiDinerProfile) => (
            <div
              key={dinerProfile.id}
              className="flex items-center justify-between p-2 bg-white rounded border"
            >
              <span>{dinerProfile.name}</span>
              <button
                className="h-7 px-2 py-2 flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-200 rounded-md"
                onClick={() => navigateToAccount(dinerProfile.accountId)}
              >
                <LuUnlink color="#f56565" size={15} />
                Unlink
              </button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const renderClientReportingPrograms = (
    clientReportingPrograms: LinkedClientReportingProgram[],
  ) => {
    if (clientReportingPrograms.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          No reporting programs linked to this account
        </div>
      )
    }

    return (
      <div className="space-y-4">
        <Accordion type="multiple" className="w-full border-0">
          {clientReportingPrograms.map(
            (reportingProgram: LinkedClientReportingProgram) => (
              <AccordionItem
                key={reportingProgram.id}
                value={`reporting-${reportingProgram.id}`}
                className="border-l-2 border-gray-200 pl-4 mb-4"
              >
                <AccordionTrigger className="py-2 hover:bg-gray-50 [&[data-state=open]]:bg-gray-50 px-2 rounded-md">
                  <div className="flex items-center gap-2">
                    <LuFileText size={20} color="#a0aec0" />
                    <div className="font-semibold">
                      Reporting Program: {reportingProgram.name}
                    </div>
                    <span
                      className="ml-2 px-2 py-1 rounded-lg"
                      style={{
                        border: '1px solid #a0aec0',
                      }}
                    >
                      {reportingProgram.dinerProfiles.length}{' '}
                      {reportingProgram.dinerProfiles.length === 1
                        ? 'profile'
                        : 'profiles'}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pl-6">
                  {renderDinerProfiles(reportingProgram.dinerProfiles)}
                </AccordionContent>
              </AccordionItem>
            ),
          )}
        </Accordion>
      </div>
    )
  }

  const renderStandaloneDinerProfiles = (dinerProfiles: ApiDinerProfile[]) => {
    if (dinerProfiles.length === 0) {
      return null
    }

    return (
      <div className="space-y-2">
        <Accordion type="multiple" className="w-full border-0">
          <AccordionItem
            value="standalone-diner-profiles"
            className="border-l-2 border-gray-200 pl-4 mb-4"
          >
            <AccordionTrigger className="py-2 hover:bg-gray-50 [&[data-state=open]]:bg-gray-50 px-2 rounded-md">
              <div className="flex items-center gap-2">
                <LuCoffee size={20} color="#a0aec0" />
                <div className="font-semibold">Standalone Diner Profiles</div>
                <span
                  className="ml-2 px-2 py-1 rounded-lg"
                  style={{
                    border: '1px solid #a0aec0',
                  }}
                >
                  {dinerProfiles.length}{' '}
                  {dinerProfiles.length === 1 ? 'profile' : 'profiles'}
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pl-6">
              {renderDinerProfiles(dinerProfiles, true)}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    )
  }

  const renderClients = (clients: LinkedAccount[]) => (
    <div className="space-y-6 pl-6">
      {clients.map((client: LinkedAccount) => (
        <div key={client.id} className="border-l-2 border-gray-200 pl-4">
          <Accordion type="multiple" className="w-full border-0">
            <AccordionItem value={`client-${client.id}`} className="border-0">
              <AccordionTrigger className="py-2 hover:bg-gray-50 [&[data-state=open]]:bg-gray-50 px-2 rounded-md">
                <div className="flex items-center gap-2 w-full">
                  <LuUsers size={20} color="#a0aec0" />
                  <div className="font-semibold">
                    Account Name: {client.name}
                  </div>
                  <button
                    className="h-7 px-2 py-2 ml-auto flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-200 rounded-md z-50"
                    onClick={(e) => {
                      e.stopPropagation()
                      onUnlinkAccount(client)
                    }}
                  >
                    <LuUnlink color="#f56565" size={15} />
                    Unlink
                  </button>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pl-6">
                {renderClientReportingPrograms(client.clientReportingPrograms)}
              </AccordionContent>
              <AccordionContent className="pl-6">
                {renderStandaloneDinerProfiles(client.dinerProfiles)}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      ))}
    </div>
  )

  const renderAccounts = () => {
    return (
      <div className="flex flex-col w-full">
        <p className="text-lg font-bold">Linked Accounts</p>
        <Accordion type="multiple" className="w-full">
          {Object.values(linkedLocations).length > 0 ? (
            Object.values(linkedLocations).map((location: LinkedLocation) => (
              <AccordionItem
                key={location.name}
                value={`location-${location.name}`}
                className="border-b last:border-0"
              >
                <AccordionTrigger className="px-4 py-3 hover:bg-gray-200 bg-gray-100">
                  <div className="flex items-center gap-2">
                    <LuBuilding size={20} color="#a0aec0" />
                    <span className="font-semibold">{location.name}</span>
                    <span
                      className="ml-2 px-2 py-1 rounded-lg"
                      style={{ border: '1px solid #a0aec0' }}
                    >
                      {location.accounts.length}{' '}
                      {location.accounts.length === 1 ? 'account' : 'accounts'}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-3">
                  {renderClients(location.accounts)}
                </AccordionContent>
              </AccordionItem>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">
              No linked accounts
            </div>
          )}
        </Accordion>
      </div>
    )
  }

  const renderLinkAccount = () => {
    return (
      <div className="flex flex-col w-full">
        <p className="text-lg font-bold">Select Account to Link User to</p>
        <div className="my-2">
          <div className="flex items-center border-2 rounded-md border-gray-400">
            <div className="px-3 py-2 border-r-2 border-gray-400">
              <IoMdCheckmark color="#cbd5e0" />
            </div>
            <input
              type="text"
              className="flex-1 px-3 py-2 outline-none"
              placeholder="Search for a client profile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={() => setOpenCombobox(true)}
            />
            <button
              className="px-3 bg-transparent border-none cursor-pointer"
              onClick={() => setOpenCombobox(!openCombobox)}
            >
              <LuChevronsUpDown color="#cbd5e0" />
            </button>
          </div>

          {openCombobox && (
            <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
              {accounts.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No client profile found
                </div>
              ) : (
                accounts.map((account, idx) => (
                  <div
                    key={idx}
                    className={`px-3 py-2 cursor-pointer flex items-center ${
                      selectedClient && selectedClient.id === account.id
                        ? 'bg-gray-100'
                        : ''
                    } hover:bg-gray-100`}
                    onClick={() => {
                      setSelectedClient(account)
                      setSearchTerm(account.name)
                      setOpenCombobox(false)
                    }}
                  >
                    {selectedClient && selectedClient.id === account.id && (
                      <IoMdCheckmark color="#48bb78" />
                    )}
                    <span>{account.name}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        {selectedClient && (
          <p className="text-sm text-green-600 mt-1">
            <IoMdCheckmark className="inline-block h-4 w-4 mr-1" />
            Selected: {selectedClient.name}
          </p>
        )}
      </div>
    )
  }

  const renderStatusTag = (valid: boolean, label: string, status: string) => {
    return valid ? (
      <span className="text-green-800 bg-green-200 px-2 py-1 rounded-lg font-semibold">
        {label}: {status}
      </span>
    ) : (
      <span className="text-orange-700 bg-orange-300 px-2 py-1 rounded-lg font-semibold">
        {label}: {status}
      </span>
    )
  }

  const onCancel = () => {
    hideModal()
  }

  const onSave = async () => {
    if (!marketplaceUser.id) {
      return
    }

    const params: UpdateMarketplaceUserParams = {
      id: marketplaceUser.id,
      firstName,
      lastName,
      email,
    }
    if (selectedClient) {
      params.accountIds = [selectedClient.id]
    }

    const updatedMarketplaceUser = await updateMarketplaceUser(params)

    if (updatedMarketplaceUser) {
      refreshMarketplaceUsers(1)
      hideModal()
    }
  }

  return (
    <Modal hideModal={hideModal} noHeader padding="10px">
      {renderHeader()}
      <div className="mt-2 w-full p-3">
        <div className="flex flex-row gap-2 justify-between">
          <Input
            label="First Name"
            width="45%"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            label="Last Name"
            width="45%"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>
        <Input
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="mt-2 w-full p-3">{renderLinkAccount()}</div>
      <div className="mt-2 w-full p-3">{renderAccounts()}</div>
      <div className="flex flex-row gap-2 justify-end mt-3">
        <button
          className="bg-white text-black border border-black px-4 py-2 rounded-md"
          style={{ fontWeight: 600 }}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          className="bg-black text-white px-4 py-2 rounded-md"
          style={{ fontWeight: 600 }}
          onClick={onSave}
        >
          Save Changes
        </button>
      </div>
      <UnlinkAccountWarningModal
        linkedDinerProfiles={linkedDinerProfiles}
        cancelUnlinkAccount={cancelUnlinkAccount}
        navigateToAccount={navigateToAccount}
      />
    </Modal>
  )
}

interface UnlinkAccountWarningModalProps {
  linkedDinerProfiles: ApiDinerProfile[] | null
  cancelUnlinkAccount: () => void
  navigateToAccount: (accountId: string) => void
}

const UnlinkAccountWarningModal: React.FC<UnlinkAccountWarningModalProps> = ({
  linkedDinerProfiles,
  cancelUnlinkAccount,
  navigateToAccount,
}) => {
  return (
    <dialog
      id="unlink-account-warning-modal"
      className="bg-yellow-300 rounded-md"
      style={{ maxWidth: '500px' }}
    >
      <div className="flex flex-row items-start gap-2">
        <div>
          <IoWarningOutline size={30} color="#b7791f" />
        </div>
        <div className="flex flex-col gap-2 text-yellow-700 font-semibold">
          This contact is associated with {linkedDinerProfiles?.length || 0}{' '}
          Diner Profile(s).
          <ul>
            {linkedDinerProfiles?.map((profile) => (
              <li key={profile.id}>
                {profile.name} Created [
                {Moment(profile.createdAt).format('MM/DD/YYYY')}]
              </li>
            ))}
          </ul>
          You must update the Diner Profiles(s) with a new default contact
          before being able to unlink this contact.
        </div>
      </div>
      <div className="flex flex-row gap-2 justify-center mt-3">
        <button
          className="bg-white text-black px-4 py-2 rounded-md"
          style={{ fontWeight: 600 }}
          onClick={() => {
            navigateToAccount(linkedDinerProfiles?.[0].accountId || '')
          }}
        >
          View Diner Profiles
        </button>
        <button
          className="bg-white text-black px-4 py-2 rounded-md"
          style={{ fontWeight: 600 }}
          onClick={cancelUnlinkAccount}
        >
          Cancel
        </button>
      </div>
    </dialog>
  )
}

export default EditMarketplaceUserModal
